import { useState, useEffect, useCallback } from 'react';
import api, { zohoAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatNumber, formatDateTime, formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  RefreshCw, Package, FileText, Loader2,
  CheckCircle, XCircle, AlertTriangle, Clock, Database, Zap, Download,
  ShoppingCart, Receipt, Search
} from 'lucide-react';

const PAGE_SIZE = 50;

const statusColor = (s) => {
  const v = (s || '').toLowerCase();
  if (['paid', 'received', 'fulfilled', 'closed'].includes(v)) return 'bg-emerald-100 text-emerald-800';
  if (['draft', 'pending'].includes(v)) return 'bg-blue-100 text-blue-800';
  if (['overdue', 'void', 'cancelled', 'rejected'].includes(v)) return 'bg-red-100 text-red-800';
  if (v.includes('partial')) return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-800';
};

export default function ZohoInventoryPage() {
  const { canAccess } = useAuth();
  const [status, setStatus] = useState(null);
  const [lineItemsStatus, setLineItemsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('items');
  const [failedDocs, setFailedDocs] = useState(null);
  const [failedDocsOpen, setFailedDocsOpen] = useState(false);
  const [failedDocsLoading, setFailedDocsLoading] = useState(false);

  // Tab data
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');

  const fetchStatus = useCallback(async () => {
    try {
      const [s, li] = await Promise.all([zohoAPI.getStatus(), zohoAPI.getLineItemsStatus()]);
      setStatus(s.data);
      setLineItemsStatus(li.data);
    } catch (e) {}
  }, []);

  const fetchCategories = useCallback(async () => {
    try { const r = await zohoAPI.getItemCategories(); setCategories(r.data.categories || []); } catch (e) {}
  }, []);

  const fetchTabData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (activeTab === 'items' && categoryFilter !== 'all') params.category = categoryFilter;
      let res;
      switch (activeTab) {
        case 'items':
          res = await zohoAPI.getItems(params);
          setData(res.data.items || []); setTotal(res.data.total || 0); break;
        case 'purchase-orders':
          res = await zohoAPI.getPurchaseOrders(params);
          setData(res.data.purchase_orders || []); setTotal(res.data.total || 0); break;
        case 'sales-orders':
          res = await zohoAPI.getSalesOrders(params);
          setData(res.data.sales_orders || []); setTotal(res.data.total || 0); break;
        case 'bills':
          res = await zohoAPI.getBills(params);
          setData(res.data.bills || []); setTotal(res.data.total || 0); break;
        case 'invoices':
          res = await zohoAPI.getInvoices(params);
          setData(res.data.invoices || []); setTotal(res.data.total || 0); break;
        default: break;
      }
    } catch (e) {} finally { setLoading(false); }
  }, [activeTab, page, search, categoryFilter]);

  useEffect(() => { fetchStatus(); fetchCategories(); }, [fetchStatus, fetchCategories]);
  useEffect(() => { setPage(0); }, [activeTab, search, categoryFilter]);
  useEffect(() => {
    const t = setTimeout(fetchTabData, 300);
    return () => clearTimeout(t);
  }, [fetchTabData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await zohoAPI.sync('full');
      toast.success('Sync started — fetching all data from Zoho. This may take 1-2 minutes.');
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const s = await zohoAPI.getStatus();
          const lastSync = s.data?.last_sync;
          if (lastSync) {
            const syncTime = new Date(lastSync);
            const now = new Date();
            if (now - syncTime < 120000) { // synced within last 2 min
              clearInterval(poll);
              setSyncing(false);
              toast.success('Sync completed successfully');
              fetchStatus(); fetchTabData();
            }
          }
        } catch(e) {}
      }, 5000);
      // Auto-stop polling after 3 minutes
      setTimeout(() => { clearInterval(poll); setSyncing(false); fetchStatus(); fetchTabData(); }, 180000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); setSyncing(false); }
  };

  const openDetail = async (record) => {
    setDetailOpen(true);
    setDetailData(null);
    setDetailLoading(true);
    try {
      let url = '';
      if (activeTab === 'purchase-orders') url = `/zoho/purchase-orders/${record.zoho_po_id}`;
      else if (activeTab === 'sales-orders') url = `/zoho/sales-orders/${record.zoho_so_id}`;
      else if (activeTab === 'bills') url = `/zoho/bills-list/${record.zoho_bill_id}`;
      else if (activeTab === 'invoices') url = `/zoho/invoices-list/${record.zoho_invoice_id}`;
      if (url) {
        const r = await api.get(url);
        setDetailData({ type: activeTab, ...r.data });
      }
    } catch (e) { toast.error('Failed to load details'); }
    finally { setDetailLoading(false); }
  };


  const handleSyncLineItems = async () => {
    setBulkSyncing(true);
    try { await zohoAPI.syncAllLineItems({ batch_size: 80 }); toast.success('Line items sync started'); }
    catch (e) { toast.error('Failed'); setBulkSyncing(false); }
  };

  const toggleFailedDocs = async () => {
    const opening = !failedDocsOpen;
    setFailedDocsOpen(opening);
    if (opening && !failedDocs) {
      setFailedDocsLoading(true);
      try {
        const r = await zohoAPI.getFailedSyncDocuments();
        setFailedDocs(r.data);
      } catch (e) { toast.error('Failed to load failed-sync documents'); }
      finally { setFailedDocsLoading(false); }
    }
  };

  useEffect(() => {
    if (!bulkSyncing) return;
    const i = setInterval(async () => {
      const r = await zohoAPI.getBulkSyncStatus();
      if (!r.data.active) setBulkSyncing(false);
      fetchStatus();
    }, 10000);
    return () => clearInterval(i);
  }, [bulkSyncing, fetchStatus]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const TABS = [
    { id: 'items', label: 'Items', icon: Package, count: status?.synced_items },
    { id: 'purchase-orders', label: 'Purchase Orders', icon: ShoppingCart, count: status?.synced_purchase_orders },
    { id: 'sales-orders', label: 'Sales Orders', icon: FileText, count: status?.synced_sales_orders || 15270 },
    { id: 'bills', label: 'Bills', icon: Receipt, count: status?.synced_bills || 17739 },
    { id: 'invoices', label: 'Invoices', icon: Receipt, count: status?.synced_invoices || 14397 },
  ];

  return (
    <div className="space-y-6" data-testid="zoho-inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Database className="h-6 w-6 text-primary" />Zoho Inventory</h1>
          <p className="text-muted-foreground text-sm">Synced data from Zoho Inventory</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { fetchStatus(); fetchTabData(); }} className="rounded-sm"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
          <Button onClick={handleSync} disabled={syncing} className="rounded-sm" data-testid="sync-btn">
            {syncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}Sync All
          </Button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {TABS.map(tab => (
          <Card key={tab.id} className="rounded-sm cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setActiveTab(tab.id)}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-1"><tab.icon className="h-4 w-4" /><span className="text-xs font-medium uppercase">{tab.label}</span></div>
              <p className="text-2xl font-bold font-mono">{formatNumber(tab.count || 0)}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Connection + Sync Info */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <div className="flex items-center gap-2">
          {status?.connected ? <CheckCircle className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />}
          {status?.connected ? 'Connected' : 'Disconnected'}
        </div>
        {status?.last_sync && <div className="flex items-center gap-2 text-muted-foreground"><Clock className="h-4 w-4" />Last sync: {formatDateTime(status.last_sync)}</div>}
      </div>

      {/* Line Items Sync */}
      <Card className="rounded-sm border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold flex items-center gap-2"><Download className="h-4 w-4 text-blue-600" />Line Items Sync</h3>
              <p className="text-xs text-muted-foreground">PO, SO, Bill, Invoice line item details</p>
            </div>
            <Button onClick={handleSyncLineItems} disabled={bulkSyncing} size="sm" className="rounded-sm bg-blue-600 hover:bg-blue-700" data-testid="sync-line-items-btn">
              {bulkSyncing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}{bulkSyncing ? 'Syncing...' : 'Sync Now'}
            </Button>
          </div>
          <div className="grid grid-cols-4 gap-4">
            {[
              { label: 'PO', data: lineItemsStatus?.po_line_items },
              { label: 'SO', data: lineItemsStatus?.so_line_items },
              { label: 'Bills', data: lineItemsStatus?.bill_line_items },
              { label: 'Invoices', data: lineItemsStatus?.invoice_line_items },
            ].map(item => (
              <div key={item.label}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{item.label}</span>
                  <span className="font-mono">{item.data?.total_line_items || 0}</span>
                </div>
                <Progress value={item.data?.percent_complete || 0} className="h-1.5" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Documents that failed to sync — admin only, read-only diagnostic */}
      {canAccess('SETTINGS', 'VIEW') && (
        <Card className="rounded-sm border-amber-200" data-testid="failed-syncs-card">
          <CardContent className="p-4">
            <button onClick={toggleFailedDocs} className="w-full flex items-center justify-between" data-testid="failed-syncs-toggle">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />Documents That Failed to Sync
                {failedDocs && <Badge variant="outline" className="ml-2">{failedDocs.total}</Badge>}
              </h3>
              <span className="text-xs text-muted-foreground">{failedDocsOpen ? 'Hide' : 'Show'}</span>
            </button>
            {failedDocsOpen && (
              <div className="mt-3">
                {failedDocsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading...</div>
                ) : !failedDocs || failedDocs.total === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="failed-syncs-empty">No failed or not-found sync documents right now.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table data-testid="failed-syncs-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>Number</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead>Attempts</TableHead>
                          <TableHead>HTTP</TableHead>
                          <TableHead>Zoho Error</TableHead>
                          <TableHead>Last Attempt</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {failedDocs.documents.map((d, i) => (
                          <TableRow key={i} data-testid={`failed-sync-row-${i}`}>
                            <TableCell className="capitalize">{d.doc_type.replace('_', ' ')}</TableCell>
                            <TableCell className="font-mono text-xs">{d.document_number || d.zoho_id}</TableCell>
                            <TableCell>
                              <Badge className={d.reason === 'not_found_in_zoho' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'}>
                                {d.reason === 'not_found_in_zoho' ? 'Not found in Zoho' : 'Sync failed'}
                              </Badge>
                            </TableCell>
                            <TableCell>{d.attempts}</TableCell>
                            <TableCell>{d.http_status ?? '—'}</TableCell>
                            <TableCell className="text-xs max-w-xs truncate" title={d.zoho_error_message || ''}>
                              {d.zoho_error_code ? `[${d.zoho_error_code}] ` : ''}{d.zoho_error_message || '—'}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{d.last_attempted_at ? formatDateTime(d.last_attempted_at) : '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border overflow-x-auto">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`zoho-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            <tab.icon className="h-4 w-4" />{tab.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${activeTab.replace('-', ' ')}...`} className="pl-10 rounded-sm" data-testid="zoho-search" />
        </div>
        {activeTab === 'items' && categories.length > 0 && (
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-full sm:w-48 rounded-sm" data-testid="zoho-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(c => (
                <SelectItem key={c.name} value={c.name}>{c.name} ({c.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Data Table */}
      <Card className="rounded-sm">
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : data.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">No records found</div>
          ) : activeTab === 'items' ? (
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead className="text-xs font-medium">SKU</TableHead>
                <TableHead className="text-xs font-medium">Name</TableHead>
                <TableHead className="text-xs font-medium">Category</TableHead>
                <TableHead className="text-xs font-medium text-right">On Hand</TableHead>
                <TableHead className="text-xs font-medium text-right">Available</TableHead>
                <TableHead className="text-xs font-medium text-right">Rate</TableHead>
                <TableHead className="text-xs font-medium">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.map((item, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-mono text-xs">{item.sku}</TableCell>
                    <TableCell className="text-sm max-w-[250px] truncate">{item.name}</TableCell>
                    <TableCell>{item.category_name ? <Badge variant="outline" className="text-xs rounded-sm whitespace-nowrap">{item.category_name}</Badge> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatNumber(item.stock_on_hand)}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-emerald-600">{formatNumber(item.available_stock)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{item.rate ? formatCurrency(item.rate) : '—'}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${statusColor(item.status)}`}>{item.status || '—'}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : activeTab === 'purchase-orders' ? (
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead className="text-xs font-medium">PO #</TableHead>
                <TableHead className="text-xs font-medium">Vendor</TableHead>
                <TableHead className="text-xs font-medium">Date</TableHead>
                <TableHead className="text-xs font-medium text-right">Total</TableHead>
                <TableHead className="text-xs font-medium">Currency</TableHead>
                <TableHead className="text-xs font-medium">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.map((po, i) => (
                  <TableRow key={i} className="cursor-pointer hover:bg-muted/50" onClick={() => openDetail(po)}>
                    <TableCell className="font-mono text-xs text-primary font-medium">{po.po_number}</TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">{po.vendor_name}</TableCell>
                    <TableCell className="text-xs">{po.order_date}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatNumber(po.total)}</TableCell>
                    <TableCell className="text-xs">{po.currency_code}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${statusColor(po.status)}`}>{po.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : activeTab === 'sales-orders' ? (
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead className="text-xs font-medium">SO #</TableHead>
                <TableHead className="text-xs font-medium">Customer</TableHead>
                <TableHead className="text-xs font-medium">Date</TableHead>
                <TableHead className="text-xs font-medium text-right">Total</TableHead>
                <TableHead className="text-xs font-medium">Currency</TableHead>
                <TableHead className="text-xs font-medium">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.map((so, i) => (
                  <TableRow key={i} className="cursor-pointer hover:bg-muted/50" onClick={() => openDetail(so)}>
                    <TableCell className="font-mono text-xs text-primary font-medium">{so.so_number}</TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">{so.customer_name}</TableCell>
                    <TableCell className="text-xs">{so.order_date}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatNumber(so.total)}</TableCell>
                    <TableCell className="text-xs">{so.currency_code}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${statusColor(so.status)}`}>{so.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : activeTab === 'bills' ? (
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead className="text-xs font-medium">Bill #</TableHead>
                <TableHead className="text-xs font-medium">Vendor</TableHead>
                <TableHead className="text-xs font-medium">Date</TableHead>
                <TableHead className="text-xs font-medium text-right">Total</TableHead>
                <TableHead className="text-xs font-medium">Currency</TableHead>
                <TableHead className="text-xs font-medium">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.map((bill, i) => (
                  <TableRow key={i} className="cursor-pointer hover:bg-muted/50" onClick={() => openDetail(bill)}>
                    <TableCell className="font-mono text-xs text-primary font-medium">{bill.bill_number}</TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">{bill.vendor_name}</TableCell>
                    <TableCell className="text-xs">{bill.bill_date}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatNumber(bill.total)}</TableCell>
                    <TableCell className="text-xs">{bill.currency_code}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${statusColor(bill.status)}`}>{bill.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : activeTab === 'invoices' ? (
            <Table>
              <TableHeader><TableRow className="bg-muted/50">
                <TableHead className="text-xs font-medium">Invoice #</TableHead>
                <TableHead className="text-xs font-medium">Customer</TableHead>
                <TableHead className="text-xs font-medium">Date</TableHead>
                <TableHead className="text-xs font-medium text-right">Total</TableHead>
                <TableHead className="text-xs font-medium">Currency</TableHead>
                <TableHead className="text-xs font-medium">Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.map((inv, i) => (
                  <TableRow key={i} className="cursor-pointer hover:bg-muted/50" onClick={() => openDetail(inv)}>
                    <TableCell className="font-mono text-xs text-primary font-medium">{inv.invoice_number}</TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">{inv.customer_name}</TableCell>
                    <TableCell className="text-xs">{inv.invoice_date}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatNumber(inv.total)}</TableCell>
                    <TableCell className="text-xs">{inv.currency_code}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs ${statusColor(inv.status)}`}>{inv.status}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {formatNumber(total)}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="rounded-sm">Previous</Button>
            <span className="flex items-center px-3 text-sm">Page {page + 1} of {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} className="rounded-sm">Next</Button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="zoho-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detailData?.type === 'purchase-orders' && <><ShoppingCart className="h-5 w-5 text-primary" />Purchase Order: {detailData?.header?.po_number}</>}
              {detailData?.type === 'sales-orders' && <><FileText className="h-5 w-5 text-primary" />Sales Order: {detailData?.header?.so_number}</>}
              {detailData?.type === 'bills' && <><Receipt className="h-5 w-5 text-primary" />Bill: {detailData?.header?.bill_number}</>}
              {detailData?.type === 'invoices' && <><Receipt className="h-5 w-5 text-primary" />Invoice: {detailData?.header?.invoice_number}</>}
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : detailData ? (
            <div className="space-y-4 overflow-auto flex-1">
              {/* Header Info */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-muted/30 rounded-sm text-sm">
                {detailData.type === 'purchase-orders' && <>
                  <div><span className="text-xs text-muted-foreground block">Vendor</span><strong>{detailData.header.vendor_name}</strong></div>
                  <div><span className="text-xs text-muted-foreground block">Date</span>{detailData.header.order_date}</div>
                  <div><span className="text-xs text-muted-foreground block">Total</span><strong className="font-mono">{formatNumber(detailData.header.total)} {detailData.header.currency_code}</strong></div>
                  <div><span className="text-xs text-muted-foreground block">Status</span><Badge variant="outline" className={`text-xs ${statusColor(detailData.header.status)}`}>{detailData.header.status}</Badge></div>
                  {detailData.header.reference_number && <div><span className="text-xs text-muted-foreground block">Reference</span>{detailData.header.reference_number}</div>}
                  {detailData.header.delivery_date && <div><span className="text-xs text-muted-foreground block">Delivery Date</span>{detailData.header.delivery_date}</div>}
                  {detailData.header.created_by_name && <div><span className="text-xs text-muted-foreground block">Created By</span>{detailData.header.created_by_name}</div>}
                </>}
                {detailData.type === 'sales-orders' && <>
                  <div><span className="text-xs text-muted-foreground block">Customer</span><strong>{detailData.header.customer_name}</strong></div>
                  <div><span className="text-xs text-muted-foreground block">Date</span>{detailData.header.order_date}</div>
                  <div><span className="text-xs text-muted-foreground block">Total</span><strong className="font-mono">{formatNumber(detailData.header.total)} {detailData.header.currency_code}</strong></div>
                  <div><span className="text-xs text-muted-foreground block">Status</span><Badge variant="outline" className={`text-xs ${statusColor(detailData.header.status)}`}>{detailData.header.status}</Badge></div>
                  {detailData.header.salesperson_name && <div><span className="text-xs text-muted-foreground block">Salesperson</span>{detailData.header.salesperson_name}</div>}
                  {detailData.header.shipment_date && <div><span className="text-xs text-muted-foreground block">Shipment Date</span>{detailData.header.shipment_date}</div>}
                </>}
                {detailData.type === 'bills' && <>
                  <div><span className="text-xs text-muted-foreground block">Vendor</span><strong>{detailData.header.vendor_name}</strong></div>
                  <div><span className="text-xs text-muted-foreground block">Date</span>{detailData.header.bill_date}</div>
                  <div><span className="text-xs text-muted-foreground block">Total</span><strong className="font-mono">{formatNumber(detailData.header.total)} {detailData.header.currency_code}</strong></div>
                  <div><span className="text-xs text-muted-foreground block">Status</span><Badge variant="outline" className={`text-xs ${statusColor(detailData.header.status)}`}>{detailData.header.status}</Badge></div>
                  {detailData.header.balance > 0 && <div><span className="text-xs text-muted-foreground block">Balance</span><span className="font-mono text-red-600">{formatNumber(detailData.header.balance)}</span></div>}
                  {detailData.header.due_date && <div><span className="text-xs text-muted-foreground block">Due Date</span>{detailData.header.due_date}</div>}
                </>}
                {detailData.type === 'invoices' && <>
                  <div><span className="text-xs text-muted-foreground block">Customer</span><strong>{detailData.header.customer_name}</strong></div>
                  <div><span className="text-xs text-muted-foreground block">Date</span>{detailData.header.invoice_date}</div>
                  <div><span className="text-xs text-muted-foreground block">Total</span><strong className="font-mono">{formatNumber(detailData.header.total)} {detailData.header.currency_code}</strong></div>
                  <div><span className="text-xs text-muted-foreground block">Status</span><Badge variant="outline" className={`text-xs ${statusColor(detailData.header.status)}`}>{detailData.header.status}</Badge></div>
                  {detailData.header.balance > 0 && <div><span className="text-xs text-muted-foreground block">Balance</span><span className="font-mono text-red-600">{formatNumber(detailData.header.balance)}</span></div>}
                  {detailData.header.due_date && <div><span className="text-xs text-muted-foreground block">Due Date</span>{detailData.header.due_date}</div>}
                </>}
              </div>

              {/* Line Items */}
              <div>
                <h3 className="text-sm font-semibold mb-2">Line Items ({detailData.line_items?.length || 0})</h3>
                {detailData.line_items?.length > 0 ? (
                  <Table>
                    <TableHeader><TableRow className="bg-muted/50">
                      <TableHead className="text-xs font-medium">SKU</TableHead>
                      <TableHead className="text-xs font-medium">Item</TableHead>
                      <TableHead className="text-xs font-medium text-right">Qty</TableHead>
                      {(detailData.type === 'purchase-orders') && <TableHead className="text-xs font-medium text-right">Received</TableHead>}
                      {(detailData.type === 'sales-orders') && <TableHead className="text-xs font-medium text-right">Shipped</TableHead>}
                      <TableHead className="text-xs font-medium text-right">Rate</TableHead>
                      <TableHead className="text-xs font-medium text-right">Amount</TableHead>
                      <TableHead className="text-xs font-medium">Currency</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {detailData.line_items.map((li, i) => (
                        <TableRow key={i} data-testid={`line-item-${i}`}>
                          <TableCell className="font-mono text-xs">{li.sku || '—'}</TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate">{li.item_name || '—'}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{formatNumber(li.quantity)}</TableCell>
                          {(detailData.type === 'purchase-orders') && <TableCell className="text-right font-mono text-sm">{formatNumber(li.quantity_received || 0)}</TableCell>}
                          {(detailData.type === 'sales-orders') && <TableCell className="text-right font-mono text-sm">{formatNumber(li.quantity_shipped || 0)}</TableCell>}
                          <TableCell className="text-right font-mono text-sm">{formatCurrency(li.rate)}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{formatCurrency(li.amount)}</TableCell>
                          <TableCell className="text-xs">{li.currency_code || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground py-4 text-center">No line items synced yet. Run Line Items Sync to extract details.</p>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
